import { storageInfo, type Area, type AssetStore } from './AssetStore';

/** Prefijo de ruta que el Service Worker sirve desde Cache Storage (ver src/sw.ts) */
export const ASSET_PATH = '/__asset/';
export const CACHE_NAMES: Record<Area, string> = { staging: 'totem-staging', live: 'totem-live' };

const reqFor = (key: string) => new Request(new URL(ASSET_PATH + key, location.origin).href);
const keyOf = (req: Request) => new URL(req.url).pathname.slice(ASSET_PATH.length);

/**
 * Plan A: Cache Storage. La UI pinta `/__asset/<clave>` y el Service Worker responde desde
 * caché (síncrono para React, cero esperas). Si la página aún no está controlada por el SW
 * (caso raro: primer arranque sin claim), cae a object URLs respaldados en disco.
 */
export class CacheAssetStore implements AssetStore {
  readonly kind = 'cache' as const;

  async init() {
    if (!('caches' in self)) throw new Error('Cache Storage no disponible');
    await caches.open(CACHE_NAMES.live);
  }

  async keys(area: Area) {
    const c = await caches.open(CACHE_NAMES[area]);
    return new Set((await c.keys()).map(keyOf));
  }

  async put(area: Area, key: string, blob: Blob) {
    const c = await caches.open(CACHE_NAMES[area]);
    await c.put(
      reqFor(key),
      new Response(blob, {
        status: 200,
        headers: { 'Content-Type': blob.type || 'image/webp', 'Content-Length': String(blob.size) },
      }),
    );
  }

  async promote(keys: string[]) {
    const staging = await caches.open(CACHE_NAMES.staging);
    const live = await caches.open(CACHE_NAMES.live);
    const have = await this.keys('live');
    for (const k of keys) {
      if (have.has(k)) continue;
      const res = await staging.match(reqFor(k));
      if (!res) throw new Error(`Falta ${k} en staging al promover`);
      await live.put(reqFor(k), res);
    }
  }

  async remove(area: Area, keys: string[]) {
    const c = await caches.open(CACHE_NAMES[area]);
    for (const k of keys) await c.delete(reqFor(k));
  }

  async clear(area: Area) {
    await caches.delete(CACHE_NAMES[area]);
    if (area === 'live') await caches.open(CACHE_NAMES.live);
  }

  async resolveUrls(keys: string[]) {
    const out = new Map<string, string>();
    if (navigator.serviceWorker?.controller) {
      for (const k of keys) out.set(k, ASSET_PATH + k);
      return out;
    }
    const live = await caches.open(CACHE_NAMES.live);
    for (const k of keys) {
      const res = await live.match(reqFor(k));
      if (res) out.set(k, URL.createObjectURL(await res.blob()));
    }
    return out;
  }

  releaseUrls(urls: Map<string, string>) {
    for (const u of urls.values()) if (u.startsWith('blob:')) URL.revokeObjectURL(u);
  }

  info() {
    return storageInfo();
  }
}
